import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Check, Flame, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useLocalCollection } from '@/hooks/useLocalCollection';
import { OfflineBanner } from '@/components/mobile/OfflineBanner';
import { PullToRefresh } from '@/components/mobile/PullToRefresh';
import { ProgressRing } from '@/components/mobile/ProgressRing';
import { HABITS_KEY, dayKey, isDoneToday, recentDays, streakOf, todayProgress, type Habit } from '@/lib/entry';
import { successFeedback, selectionFeedback } from '@/lib/native';
import { TONES } from '@/brand/tones';
import { cn } from '@/lib/utils';

/**
 * Today. The screen the app opens on, so it has to answer "what do I do now" in one glance and let
 * the answer be acted on in one tap.
 *
 * Built from the UI kit (`components/ui`), which takes its colour from the brand tokens — so this
 * screen never hard-codes an appearance and re-skins with the brand. Everything renders from local
 * storage, so it is complete on first paint with no network, which is what carries the app through
 * App Review's Airplane Mode check.
 */
export function HomeScreen() {
	const { items: habits, update, loaded } = useLocalCollection<Habit>(HABITS_KEY);
	const [justDone, setJustDone] = useState<string | null>(null);
	const days = useMemo(() => recentDays(7), []);
	const progress = todayProgress(habits);
	const doneCount = habits.filter(isDoneToday).length;

	const toggle = (habit: Habit) => {
		const today = dayKey();
		const done = isDoneToday(habit);
		update(habit.id, {
			done: done ? habit.done.filter((d) => d !== today) : [...habit.done, today],
		} as Partial<Habit>);

		// Completing is the moment worth marking; un-completing is a correction, so it only ticks.
		if (done) {
			void selectionFeedback();
		} else {
			void successFeedback();
			setJustDone(habit.id);
			setTimeout(() => setJustDone(null), 600);
		}
	};

	// In an app with a backend this is where the sync goes; the gesture is expected either way.
	const refresh = async () => {
		await new Promise((resolve) => setTimeout(resolve, 450));
	};

	return (
		<PullToRefresh onRefresh={refresh}>
			<div className="flex flex-col">
				<OfflineBanner />

				<header className="flex items-center justify-between gap-4 px-5 pt-6">
					<div className="min-w-0">
						<h1 className="text-[28px] font-semibold leading-tight" style={{ fontFamily: 'var(--brand-display)' }}>
							Today
						</h1>
						<p className="text-muted-foreground mt-0.5 text-sm">
							{habits.length === 0
								? 'Nothing tracked yet'
								: doneCount === habits.length
									? 'All done — nice work'
									: `${doneCount} of ${habits.length} done`}
						</p>
					</div>
					<ProgressRing value={progress} size={56}>
						<span className="text-[13px] font-semibold tabular-nums">{Math.round(progress * 100)}</span>
					</ProgressRing>
				</header>

				{/* Week strip: context for the streak, and proof the app remembers. */}
				{habits.length > 0 && (
					<div className="mt-5 flex justify-between px-5">
						{days.map((day) => {
							const completed = habits.filter((h) => h.done.includes(day.key)).length;
							const ratio = habits.length ? completed / habits.length : 0;
							return (
								<div key={day.key} className="flex flex-col items-center gap-1.5">
									<span className="text-muted-foreground text-[11px]">{day.label}</span>
									<span
										className={cn(
											'flex size-8 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums',
											ratio === 0 ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground',
											day.isToday && 'ring-primary ring-2 ring-offset-2 ring-offset-background',
										)}
										style={{ opacity: ratio === 0 ? 1 : 0.4 + ratio * 0.6 }}
									>
										{completed || ''}
									</span>
								</div>
							);
						})}
					</div>
				)}

				<div className="mt-6 flex flex-col gap-2.5 px-5">
					{!loaded && <p className="text-muted-foreground text-sm">Loading…</p>}

					{loaded && habits.length === 0 && (
						<Card>
							<CardContent className="flex flex-col items-center gap-3 px-6 py-12 text-center">
								<p className="text-base font-medium">Pick one thing to do daily</p>
								<p className="text-muted-foreground max-w-[28ch] text-sm">
									Streaks build from one repeated action. Start with a habit small enough that a bad day
									cannot break it.
								</p>
								<Button asChild size="lg" className="mt-1 rounded-full">
									<Link to="/add">
										<Plus className="size-4" />
										Add a habit
									</Link>
								</Button>
							</CardContent>
						</Card>
					)}

					{habits.map((habit) => {
						const done = isDoneToday(habit);
						const streak = streakOf(habit);
						const tone = TONES[habit.tone % TONES.length];
						return (
							<Card
								key={habit.id}
								className="overflow-hidden transition-transform active:scale-[0.99]"
								style={{ animation: justDone === habit.id ? 'habitPop 600ms ease-out' : undefined }}
							>
								{/* Card is a plain div in the kit (no asChild), so the button lives inside it and
								    fills it — that keeps the whole row tappable without forking the component. */}
								<button onClick={() => toggle(habit)} aria-pressed={done} className="w-full text-left">
									<CardContent className="flex min-h-[68px] items-center gap-3.5 px-4 py-3">
										<span
											className="flex size-10 shrink-0 items-center justify-center rounded-full text-white transition-colors"
											style={{
												background: done ? tone : 'transparent',
												border: done ? 'none' : `2px solid ${tone}55`,
											}}
										>
											{done && <Check className="size-5" strokeWidth={3} />}
										</span>

										<span className="min-w-0 flex-1">
											<span className={cn('block truncate font-medium', done && 'opacity-55')}>
												{habit.name}
											</span>
											<span className="text-muted-foreground mt-0.5 flex items-center gap-1 text-[13px]">
												{streak > 0 ? (
													<>
														<Flame className="size-3.5" style={{ color: tone }} />
														<span className="tabular-nums">
															{streak} day{streak === 1 ? '' : 's'}
														</span>
													</>
												) : (
													'Start today'
												)}
											</span>
										</span>
									</CardContent>
								</button>
							</Card>
						);
					})}
				</div>

				<style>{`
					@keyframes habitPop {
						0%   { transform: scale(1); }
						35%  { transform: scale(1.02); }
						100% { transform: scale(1); }
					}
					@media (prefers-reduced-motion: reduce) {
						@keyframes habitPop { 0%, 100% { transform: none; } }
					}
				`}</style>
			</div>
		</PullToRefresh>
	);
}
